import React from 'react';
import type { SkeletonShape } from '../utils/skeletonShape';

/**
 * Esqueletos de carga con la forma del widget.
 *
 * <b>Por qué no un spinner.</b> El tablero pinta seis u ocho tarjetas a la vez y
 * cada una responde cuando responde su propio endpoint (cada widget tiene su
 * `useQuery`). Con un spinner genérico, durante los primeros segundos la pantalla
 * es una rejilla de ruletas idénticas: el usuario no sabe qué está llegando ni
 * cuánto va a ocupar, y la grilla salta cuando el contenido real entra con otra
 * altura.
 *
 * Un esqueleto que respeta la forma resuelve las dos cosas — reserva el espacio y
 * anticipa qué va a aparecer ahí — y cuesta lo mismo.
 *
 * <b>El pulso se anula bajo `prefers-reduced-motion`.</b> Una rejilla de bloques
 * latiendo a la vez es justo el patrón que dispara molestias vestibulares.
 */

/**
 * Bloque gris del esqueleto. Sin animación propia: la pone el contenedor.
 *
 * Usa `--dash-skeleton` y no `--dash-surface-page`: en modo oscuro la superficie de página es
 * MÁS oscura que la de la tarjeta, así que el esqueleto invertía su polaridad y se leía como un
 * hueco negro en vez de como un bloque de contenido por llegar.
 */
const Bar: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className = '', style }) => (
  <div
    className={`rounded ${className}`}
    style={{ background: 'var(--dash-skeleton)', ...style }}
  />
);

/** Alturas de las barras del esqueleto de gráfico. Fijas: un `Math.random()` haría que cada render moviera el esqueleto. */
const BAR_HEIGHTS = ['45%', '70%', '55%', '85%', '40%', '65%'];
const TABLE_WIDTHS = ['38%', '22%', '18%', '14%'];

interface WidgetSkeletonProps {
  shape?: SkeletonShape;
  /** Anula el pulso. Lo resuelve el contenedor con `prefers-reduced-motion`. */
  reducedMotion?: boolean;
}

export const WidgetSkeleton: React.FC<WidgetSkeletonProps> = ({
  shape = 'generic',
  reducedMotion = false,
}) => {
  const pulse = reducedMotion ? '' : 'animate-pulse';

  return (
    <div
      className={`w-full h-full p-4 ${pulse}`}
      data-testid="widget-skeleton"
      data-shape={shape}
      aria-hidden="true"
    >
      {shape === 'kpi' && (
        <div className="h-full flex flex-col justify-center gap-3">
          <Bar className="h-8 w-2/5" />
          <Bar className="h-6 w-full" style={{ opacity: 0.6 }} />
          <Bar className="h-3 w-1/3" style={{ opacity: 0.6 }} />
        </div>
      )}

      {shape === 'line' && (
        <div className="h-full flex flex-col justify-end gap-2">
          <Bar className="h-px w-full" style={{ opacity: 0.8 }} />
          <Bar className="h-px w-full" style={{ opacity: 0.8 }} />
          <Bar className="h-px w-full" style={{ opacity: 0.8 }} />
          <Bar className="h-2 w-1/2 mt-2" style={{ opacity: 0.6 }} />
        </div>
      )}

      {shape === 'bars' && (
        <div className="h-full flex items-end justify-around gap-2">
          {BAR_HEIGHTS.map((h, i) => (
            <Bar key={i} className="flex-1" style={{ height: h, maxWidth: 48 }} />
          ))}
        </div>
      )}

      {shape === 'donut' && (
        <div className="h-full flex items-center justify-center">
          <div
            className="rounded-full"
            style={{
              width: 'min(60%, 140px)',
              aspectRatio: '1',
              border: '18px solid var(--dash-skeleton)',
            }}
          />
        </div>
      )}

      {shape === 'table' && (
        <div className="h-full flex flex-col gap-3">
          {/* El encabezado es más tenue: anticipa que ahí van etiquetas, no datos. */}
          <div className="flex gap-3">
            {TABLE_WIDTHS.map((w, i) => (
              <Bar key={i} className="h-2" style={{ width: w, opacity: 0.5 }} />
            ))}
          </div>
          {[0, 1, 2, 3].map(row => (
            <div key={row} className="flex gap-3">
              {TABLE_WIDTHS.map((w, i) => (
                <Bar key={i} className="h-3" style={{ width: w }} />
              ))}
            </div>
          ))}
        </div>
      )}

      {shape === 'image' && <Bar className="w-full h-full" />}

      {shape === 'generic' && (
        <div className="h-full flex flex-col gap-3">
          <Bar className="h-4 w-1/3" />
          <Bar className="flex-1 w-full" style={{ opacity: 0.6 }} />
        </div>
      )}
    </div>
  );
};
